import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import PlaceholderErrorModal from './PlaceholderErrorModal';

/**
 * Builds a `GenericErrorBoundary` fallback component bound to a specific
 * background skeleton. The returned fallback closes over `placeholderComponent`
 * so the only per-surface customisation is the skeleton rendered behind the
 * error modal.
 * @param {React.ComponentType} placeholderComponent - Skeleton component
 *   rendered behind the modal for the surface being protected.
 * @param {string|null} bodyText - Optional custom modal body message forwarded
 *  to `PlaceholderErrorModal`. When null, the modal renders its default body.
 * @returns {React.ComponentType} fallback component consumed by
 *   `GenericErrorBoundary`.
 */
function withPlaceholder(placeholderComponent, bodyText = null) {
  /**
   * Fallback UI rendered by `GenericErrorBoundary` when a child render throws.
   * Receives `clearBoundaryFunc`, `error`, `errorInfo`, and `resetFunc` from
   * the boundary and exposes the captured component stack via the modal's
   * details section.
   * @param {object} props - Component props.
   * @param {Function} props.clearBoundaryFunc - Resets the boundary state
   *   without invoking the retry callback. Called after a successful cancel.
   * @param {Error} props.error - Captured render error.
   * @param {object|null} props.errorInfo - React error info. Initially null
   *   between `getDerivedStateFromError` and `componentDidCatch`, then
   *   populated with the component stack.
   * @param {Function|null} props.onCancelFunc - Optional user-supplied cancel
   *   handler. Return true once the source error has been cleared so the
   *   boundary can reset safely.
   * @param {Function} props.resetFunc - Boundary reset callback that clears the
   *   boundary state and invokes the user-supplied retry callback.
   * @param {boolean} props.retryDisabled - Disables retry when retry cannot
   *   succeed.
   * @returns {React.ReactNode} placeholder error fallback UI.
   */
  function PlaceholderErrorFallback({
    clearBoundaryFunc, error, errorInfo = null, onCancelFunc = null, resetFunc,
    retryDisabled = false,
  }) {
    const details = useMemo(() => ({
      componentStack: errorInfo?.componentStack || null,
      message: error?.message || null,
      stack: error?.stack || null,
    }), [error, errorInfo]);

    const handleCancelFunc = () => {
      const errorCleared = onCancelFunc?.() === true;
      if (errorCleared) {
        clearBoundaryFunc();
      }
    };

    return (
      <PlaceholderErrorModal
        bodyText={bodyText ?? undefined}
        details={details}
        onCancelFunc={handleCancelFunc}
        onRetryFunc={resetFunc}
        placeholderComponent={placeholderComponent}
        retryDisabled={retryDisabled}
      />
    );
  }

  PlaceholderErrorFallback.propTypes = {
    clearBoundaryFunc: PropTypes.func.isRequired,
    error: PropTypes.instanceOf(Error).isRequired,
    errorInfo: PropTypes.shape({
      componentStack: PropTypes.string,
    }),
    onCancelFunc: PropTypes.func,
    resetFunc: PropTypes.func.isRequired,
    retryDisabled: PropTypes.bool,
  };

  PlaceholderErrorFallback.displayName = 'PlaceholderErrorFallback';

  return PlaceholderErrorFallback;
}

export default withPlaceholder;
